/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { Localization } from 'connex-cds';
import { find, get, keys, pickBy } from 'lodash';
import React from 'react';

import { useGetCompanyAdmin } from '../../../../../query-hooks/company';
import { formatTicketDateTime } from '../formatTicketDateTime';
import { buildTemplateContext } from './buildTemplateContext';
import { getTicketWorkTypeName } from './getTicketWorkTypeName';

const initializeBatchDataKeys = () => {
  const initialObject = {};
  for (let i = 0; i < 15; i++) {
    initialObject[`BatchInfoCode${i}`] = null;
    initialObject[`BatchInfoDescription${i}`] = null;
    initialObject[`BatchInfoTargetQty${i}`] = null;
    initialObject[`BatchInfoTargetUOM${i}`] = null;
    initialObject[`BatchInfoActualQty${i}`] = null;
    initialObject[`BatchInfoActualUOM${i}`] = null;
    initialObject[`BatchInfoDesignSSDQty${i}`] = null;
    initialObject[`BatchInfoDesignSSDUOM${i}`] = null;
  }
  return initialObject;
};

const getIndexedContextKeys = (ticket, keyPrefix, pathTemplate, maxIndex) => {
  const indexed = {};

  for (let i = 0; i <= maxIndex; i++) {
    indexed[`${keyPrefix}${i}`] = get(ticket, pathTemplate.replace('{i}', i));
  }

  return indexed;
};

/** Batch consumptions to `BatchInfo*` template tokens. Pure, so a sync can recompute these
 * from a freshly fetched coc-setup without waiting on a render. */
export const buildBatchData = (cocData = []) =>
  (cocData || []).reduce((accumulator, consumption, index) => {
    accumulator[`BatchInfoCode${index}`] = consumption?.item?.id;
    accumulator[`BatchInfoDescription${index}`] = consumption?.item?.description;
    accumulator[`BatchInfoTargetQty${index}`] = consumption?.targetQuantity?.value;
    accumulator[`BatchInfoTargetUOM${index}`] = consumption?.targetQuantity?.uomCode;
    accumulator[`BatchInfoActualQty${index}`] = consumption?.actualQuantity?.value;
    accumulator[`BatchInfoActualUOM${index}`] = consumption?.actualQuantity?.uomCode;
    accumulator[`BatchInfoDesignSSDQty${index}`] = consumption?.designSSDQuantity?.value;
    accumulator[`BatchInfoDesignSSDUOM${index}`] = consumption?.designSSDQuantity?.uomCode;

    return accumulator;
  }, initializeBatchDataKeys());

export const usePreparedContext = (props = {}) => {
  const { ticket, mobileTicket, cocData = [], pdfContext, timeZone } = props;

  const batchdata = React.useMemo(() => buildBatchData(cocData), [cocData]);

  const translateMessage = Localization.useTranslateMessage();
  const { userLocale } = Localization.useLocalizationContext() ?? {};
  const { data: companyAdmin } = useGetCompanyAdmin();

  const dateTimeSettings = companyAdmin?.dateTimeSettings ?? {};
  const { dateFormat, use24HourTimeFormat } = dateTimeSettings;

  // Safe outside TicketContext (setup QR editor): timeZone comes from props when available.
  const formatLongDateTime = React.useCallback(
    isoDateString =>
      formatTicketDateTime(isoDateString, {
        dateFormat,
        use24HourTimeFormat,
        timeZone,
        mode: 'datetime',
        locale: userLocale,
      }),
    [dateFormat, timeZone, use24HourTimeFormat, userLocale]
  );

  const primaryLineItem = React.useMemo(() => {
    return find(ticket?.lineItems, { isPrimary: true });
  }, [ticket?.lineItems]);

  const splitLoadLineItems = React.useMemo(() => {
    return pickBy(ticket?.lineItems, { isSplitLoad: true });
  }, [ticket?.lineItems]);

  const lineItemValue = (itemObject, itemField, subField) => {
    const getLineItemValue = lineItem => {
      const item = lineItem?.[itemObject];
      const field = item?.[itemField];
      if (subField) return field?.[subField];
      if (itemObject.toLowerCase().includes('quantity') && itemField === 'value' && item?.uomCode) {
        return translateMessage(`${item?.uomCode}_short`, { value: field });
      }
      return field;
    };

    if (Object.keys(splitLoadLineItems).length > 0) {
      return Object.values(splitLoadLineItems).map(getLineItemValue).filter(Boolean).join(', ');
    }

    return getLineItemValue(primaryLineItem);
  };

  const splitContainers = React.useMemo(() => ticket?.containers ?? {}, [ticket?.containers]);

  const getContainerValue = (firstPath, secondPath, thirdPath) => {
    const getLineItemValue = containers => {
      const item = containers?.[firstPath];
      const field = item?.[secondPath];
      if (thirdPath) return field?.[thirdPath];
      const isQuantity = [
        firstPath.toLowerCase().includes('quantity'),
        firstPath.toLowerCase().includes('weight'),
      ].some(Boolean);
      if (isQuantity && secondPath === 'value' && item?.uomCode) {
        return translateMessage(`${item?.uomCode}_short`, { value: field });
      }
      return field;
    };

    if (Object.keys(splitContainers).length > 0) {
      return Object.values(splitContainers).map(getLineItemValue).filter(Boolean).join(', ');
    }

    const splitLoadContainer = get(splitContainers, '[0]', {});
    return getLineItemValue(splitLoadContainer);
  };

  const splitCustomDataValue = (path, itinerant) => {
    const value = get(ticket, path);
    const elements = keys(value)
      .filter(key => key.includes(itinerant))
      .map(key => get(value, key));

    return elements?.join(', ');
  };

  const getTicketEvents = isFormatted => {
    // Build an object that contains the formatted ticket event time strings.
    // An asterisk indicates an eventDateTime that has been edited by the driver.
    const merged = { ...ticket?.ticketEvents, ...mobileTicket?.ticketEvents };
    return Object.keys(merged).reduce(
      (acc, key) => {
        const ticketDt = ticket?.ticketEvents?.[key]?.eventDateTime;
        const mobileTicketDt = merged?.[key]?.eventDateTime;
        if (mobileTicketDt)
          acc[key] = `${isFormatted ? formatLongDateTime(mobileTicketDt) : mobileTicketDt}${
            ticketDt !== mobileTicketDt ? '*' : ''
          }`;
        return acc;
      },
      { PRINTED: '', TO_JOB: '', ARRIVE_JOB: '', UNLOADING: '', END_UNLOADING: '', LEAVE_JOB: '', IN_YARD: '' }
    );
  };

  const ticketEvents = React.useMemo(() => getTicketEvents(false), [mobileTicket?.ticketEvents, ticket?.ticketEvents]);

  const ticketEventsFormatted = React.useMemo(
    () => getTicketEvents(true),
    [formatLongDateTime, mobileTicket?.ticketEvents, ticket?.ticketEvents]
  );

  const getTruckSeal = React.useCallback(() => {
    const getTruckSealValue = container => container?.vehicle?.truckSeal;

    if (Object.keys(splitContainers).length > 0) {
      const values = Object.values(splitContainers)
        .map(getTruckSealValue)
        .filter(v => v !== undefined && v !== null && v !== '');

      return values.length > 0 ? values[0] : '';
    }

    const splitLoadContainer = get(splitContainers, '[0]', {});
    const seal = getTruckSealValue(splitLoadContainer);

    return seal !== undefined && seal !== null ? seal : '';
  }, [splitContainers]);

  const getSecuenceDataValue = React.useCallback(
    fieldPrompt => {
      const paths = [
        'customData.CS.userDefined.ticket',
        'customData.CS.userDefined.order',
        'customData.CS.userDefined.plant',
      ];

      for (const path of paths) {
        const raw = get(ticket, path, {});
        const entries = Array.isArray(raw) ? raw : Object.values(raw || {});
        const found = find(entries, { field: fieldPrompt });

        if (found?.value !== undefined && found?.value !== null && found?.value !== '') {
          return found.value;
        }
      }

      return '';
    },
    [ticket]
  );

  /* eslint-disable react-hooks/exhaustive-deps */
  const preparedContext = React.useMemo(() => {
    const mergedTicket = buildTemplateContext({ ticket, cocData });
    const context = {
      printed: ticketEvents?.PRINTED,
      loadingStarted: ticketEvents?.LOADING_STARTED,
      loadingComplete: ticketEvents?.LOADING_COMPLETE,
      toJob: ticketEvents?.TO_JOB,
      arriveJob: ticketEvents?.ARRIVE_JOB,
      unloading: ticketEvents?.UNLOADING,
      endUnloading: ticketEvents?.END_UNLOADING,
      leaveJob: ticketEvents?.LEAVE_JOB,
      inYard: ticketEvents?.IN_YARD,
      pumpingStarted: ticketEvents?.PUMPING_STARTED,
      pumpingComplete: ticketEvents?.PUMPING_COMPLETE,
      readyToPump: ticketEvents?.READY_TO_PUMP,

      'dateTime::printed': ticketEventsFormatted?.PRINTED,
      'dateTime::loadingStarted': ticketEventsFormatted?.LOADING_STARTED,
      'dateTime::loadingComplete': ticketEventsFormatted?.LOADING_COMPLETE,
      'dateTime::toJob': ticketEventsFormatted?.TO_JOB,
      'dateTime::arriveJob': ticketEventsFormatted?.ARRIVE_JOB,
      'dateTime::unloading': ticketEventsFormatted?.UNLOADING,
      'dateTime::endUnloading': ticketEventsFormatted?.END_UNLOADING,
      'dateTime::leaveJob': ticketEventsFormatted?.LEAVE_JOB,
      'dateTime::inYard': ticketEventsFormatted?.IN_YARD,
      'dateTime::pumpingStarted': ticketEventsFormatted?.PUMPING_STARTED,
      'dateTime::pumpingComplete': ticketEventsFormatted?.PUMPING_COMPLETE,
      'dateTime::readyToPump': ticketEventsFormatted?.READY_TO_PUMP,

      loadedQuantity: lineItemValue('quantity', 'value'),
      orderedQuantity: lineItemValue('orderedQuantity', 'value'),
      ticketedQuantity: lineItemValue('ticketedQuantity', 'value'),
      productId: lineItemValue('item', 'id'),
      productDescription: lineItemValue('item', 'description'),
      'origin.description': mergedTicket?.origin?.description,
      'customerParty.id': mergedTicket?.customerParty?.id,
      sapDeliveryNumber: mergedTicket?.id,
      salesOrderNumber: mergedTicket?.originatorRecordId,
      wcContactDateTime: mergedTicket?.customData?.productDetails?.wcContactDateTime,
      'dateTime::wcContactDateTime': mergedTicket?.customData?.productDetails?.wcContactDateTime
        ? formatLongDateTime(mergedTicket?.customData?.productDetails?.wcContactDateTime)
        : '',
      purchaseOrder: mergedTicket?.purchaseOrder,
      customerPoNumber: mergedTicket?.customData?.orderDetails?.customerPoNumber,
      orderPlacedBy: mergedTicket?.customData?.orderDetails?.orderPlaceBy,
      orderPlacedByPhone: mergedTicket?.customData?.orderDetails?.orderPlaceByPhone,
      vehicleId: mergedTicket?.sk?.vehicleId,
      totalLoadedQuantity: mergedTicket?.dispatchOrder?.totalTicketed?.uomCode
        ? translateMessage(`${mergedTicket?.dispatchOrder?.totalTicketed?.uomCode}_short`, {
            value: mergedTicket?.dispatchOrder?.totalTicketed?.value,
          })
        : '',
      loadSpacing: mergedTicket?.dispatchOrder?.deliverySchedule?.deliverySpacing?.value,
      deliveryFlow: mergedTicket?.customData?.orderDetails?.deliveryFlow,
      workType: getTicketWorkTypeName(mergedTicket?.workType),
      unloadingMethod: mergedTicket?.unloadingMethod?.description,
      unloadingTime: mergedTicket?.customData?.orderDetails?.unloadingTime,
      matSalesText: splitCustomDataValue('customData.productDetails', 'matSalesText'),
      matSalesText1: mergedTicket?.customData?.productDetails?.matSalesText1,
      matSalesText2: mergedTicket?.customData?.productDetails?.matSalesText2,
      matSalesText3: mergedTicket?.customData?.productDetails?.matSalesText3,
      strengthClass: mergedTicket?.customData?.productDetails?.strengthClass,
      environmentExposure: mergedTicket?.customData?.productDetails?.environmentExposure,
      metExposureClass: mergedTicket?.customData?.productDetails?.metExposureClass,
      dmax: mergedTicket?.customData?.productDetails?.dmax,
      standardCert: mergedTicket?.customData?.productDetails?.certificationText,
      specification: mergedTicket?.customData?.productDetails?.specification,
      workabilityTarget: mergedTicket?.customData?.productDetails?.customerSlumpTarget,
      stoneQuality: mergedTicket?.customData?.productDetails?.stoneQuality,
      sandQuality: mergedTicket?.customData?.productDetails?.sandQuality,
      targetWc: mergedTicket?.customData?.productDetails?.targetWcRatioRecipe,
      controlClass: mergedTicket?.customData?.productDetails?.controlClass,
      chlorideClass: mergedTicket?.customData?.productDetails?.chlorideClass,
      strengthClass1: mergedTicket?.customData?.productDetails?.strengthClass1,
      strengthClass2: mergedTicket?.customData?.productDetails?.strengthClass2,
      additionType: mergedTicket?.customData?.productDetails?.additionType,
      admixtureType: mergedTicket?.customData?.productDetails?.admixtureType,
      orderedTemp: mergedTicket?.customData?.productDetails?.orderedTemperature,
      fiber1Type: mergedTicket?.customData?.productDetails?.fiber1Type,
      fiber2Type: mergedTicket?.customData?.productDetails?.fiber2Type,
      calculatedMaturityTime: mergedTicket?.customData?.productDetails?.maturityColdHot,
      mixIdentity: lineItemValue('item', 'mix', 'id'),
      grade: mergedTicket?.customData?.productDetails?.grade,
      mixType: lineItemValue('item', 'mix', 'type'),
      maxAggSize: mergedTicket?.customData?.productDetails?.maxAggSize,
      maxAggType: mergedTicket?.customData?.productDetails?.maxAggType,
      cementType: lineItemValue('production', 'cementType'),
      admixtures: mergedTicket?.customData?.productDetails?.admixtures,
      addmixDescription: mergedTicket?.customData?.productDetails?.addmixDescription,
      slump: lineItemValue('properties', 'slump'),
      minCementContent: lineItemValue('production', 'minCementContent'),
      maxWatCemRatio: lineItemValue('specifiedProperties', 'maxWatCemRatio'),
      maxWaterToAdd: lineItemValue('production', 'maxWaterToAdd', 'value'),
      mortarMinWorkingLife: lineItemValue('production', 'mortarMinWorkingLife'),
      code: mergedTicket?.customData?.productDetails?.code,
      dcClass: mergedTicket?.customData?.productDetails?.dcClass,
      chloride: mergedTicket?.customData?.productDetails?.chloride,
      signedByHanson: mergedTicket?.customData?.productDetails?.signedByHanson,
      warmStamp: mergedTicket?.customData?.productDetails?.warmStamp,
      coldStamp: mergedTicket?.customData?.productDetails?.coldStamp,
      containsSikatard: mergedTicket?.customData?.productDetails?.containsSikatard,
      containsDelvo: mergedTicket?.customData?.productDetails?.containsDelvo,
      steNoDescription: mergedTicket?.customData?.productDetails?.steNoDescription,
      soldToNumber: mergedTicket?.customData?.orderDetails?.soldToNumber,
      consistencyClass: mergedTicket?.customData?.productDetails?.consistencyClass,
      vctekvMax: mergedTicket?.customData?.productDetails?.vctekvMax,
      minAirContent: mergedTicket?.customData?.productDetails?.minAirContent,
      plantCode: mergedTicket?.origin?.id,
      shipToCode: mergedTicket?.destination?.id,
      customerShipToPoNumber: mergedTicket?.customData?.orderDetails?.customerShipToPoNumber,
      tareWeight: getContainerValue('tareWeight', 'value'),
      grossWeight: getContainerValue('grossWeight', 'value'),
      referenceNumber: splitCustomDataValue('customData', 'referenceNumber'),
      truckSeal: getTruckSeal(),
      millCertification: getSecuenceDataValue('MillCertification'),
      orderCode: getSecuenceDataValue('OrderCode'),
      sequence: getSecuenceDataValue('Sequence'),
      superPType: getSecuenceDataValue('SuperPType'),
      id: mergedTicket?.id,
      slumpMin: primaryLineItem?.mixInfo?.minSlump,
      slumpMax: primaryLineItem?.mixInfo?.maxSlump,
      slumpNominal: primaryLineItem?.mixInfo?.targetSlump,
      airMin: primaryLineItem?.mixInfo?.minAirContent,
      airMax: primaryLineItem?.mixInfo?.maxAirContent,
      airNominal: primaryLineItem?.mixInfo?.targetAirContent,
      'driver.id': mergedTicket?.driver?.id,
      'customerParty.name': mergedTicket?.customerParty?.name,
      'driver.firstName': mergedTicket?.driver?.firstName,
      'driver.lastName': mergedTicket?.driver?.lastName,
      'dispatchOrder.id': mergedTicket?.dispatchOrder?.id,
      'sk.projectId': mergedTicket?.sk?.projectId,
      'origin.contacts.0.phoneNumber': pdfContext?.['origin_contacts_0_phoneNumber'],
      'destination.address.address1': mergedTicket?.destination?.address?.address1,
      'destination.instructions': mergedTicket?.destination?.instructions,
      'dateTime::dispatchDateTime': mergedTicket?.dispatchDateTime
        ? formatLongDateTime(mergedTicket?.dispatchDateTime)
        : '',
      'origin.id': mergedTicket?.origin?.id,
      customerJob: pdfContext?.customerJob,
      actualWatCemRatio: pdfContext?.['batchResults_actualWatCemRatio'],
      ...batchdata,

      ...getIndexedContextKeys(mergedTicket, 'orderMessages', 'customData.CS.messageText.{i}', 10),
      orderedByPhone: mergedTicket?.customData?.CS?.orderDetails?.orderedByPhone,
      ...getIndexedContextKeys(mergedTicket, 'surchargeCode', 'customData.CS.charges.surcharge.{i}.id', 10),
      customerAccountType: mergedTicket?.customData?.CS?.orderDetails?.customerAccountType,
      priceExtensionCode: primaryLineItem?.item?.priceExtension?.code,
      distance: mergedTicket?.destination?.siteDistance?.value,
      payDistance: mergedTicket?.destination?.payDistance?.value,
      subtotalPrice: mergedTicket?.priceSummary?.subtotalPrice,
      taxAmount: mergedTicket?.priceSummary?.taxAmount,
      codOrderAmt: mergedTicket?.customData?.CS?.orderDetails?.cod_order_amt,
      resoldActualBatchTime: mergedTicket?.customData?.CS?.resoldConcrete?.actualBatchTime,
      csBatchTime: mergedTicket?.customData?.CS?.productDetails?.wcContactDateTime,

      'dateTime::resoldActualBatchTime': mergedTicket?.customData?.CS?.resoldConcrete?.actualBatchTime
        ? formatLongDateTime(mergedTicket?.customData?.CS?.resoldConcrete?.actualBatchTime)
        : '',
      'dateTime::csBatchTime': mergedTicket?.customData?.CS?.productDetails?.wcContactDateTime
        ? formatLongDateTime(mergedTicket?.customData?.CS?.productDetails?.wcContactDateTime)
        : '',

      companyName: mergedTicket?.origin?.companyName,
      receiptCode: mergedTicket?.customData?.CS?.receiptCode,
      mixClassCode: mergedTicket?.customData?.CS?.productDetails?.strengthClass?.id,
      mixClassLongDescription: mergedTicket?.customData?.CS?.productDetails?.strengthClass?.name,
      printableField: mergedTicket?.customData?.CS?.productDetails?.cementTypeClass?.printableField,
      cementClassStrengthClass: mergedTicket?.customData?.CS?.productDetails?.cementTypeClass?.class,
      cementClassSubClass: mergedTicket?.customData?.CS?.productDetails?.cementTypeClass?.subclass,
      cementReplacementPct: mergedTicket?.customData?.CS?.productDetails?.cementTypeClass?.replacementPercentage,
      aggClassCode: mergedTicket?.customData?.CS?.productDetails?.coarseAggregateClass?.id,
      admixClassLongDescription: mergedTicket?.customData?.CS?.productDetails?.addmixClass?.name,
      batchBookLongDescription: mergedTicket?.customData?.CS?.productDetails?.batchBookType?.name,
      consistenceClassId: mergedTicket?.customData?.CS?.productDetails?.consistenceClass?.id,
      consistenceClassLongDescription: mergedTicket?.customData?.CS?.productDetails?.consistenceClass?.nameOther,
      slumpFromMixConsistenceCode: mergedTicket?.customData?.CS?.productDetails?.consistenceClass?.nominalWorkability,
      minimumCementContent: mergedTicket?.customData?.CS?.productDetails?.minimumCementContent,
      maximumWaterCementRatio: mergedTicket?.customData?.CS?.productDetails?.maximumWaterCementRatio,
      dcClassId: mergedTicket?.customData?.CS?.productDetails?.dcClass?.id,
      chlorideClassIdTqs: mergedTicket?.customData?.CS?.productDetails?.chlorideClass?.id,
      chlorideClassIdQc: mergedTicket?.customData?.CS?.productDetails?.chlorideClass?.name,
      maximumAggSize: mergedTicket?.customData?.CS?.productDetails?.maximumAggSize,
      pValue: mergedTicket?.customData?.CS?.productDetails?.pValue,
      exposureClass: mergedTicket?.customData?.CS?.exposureClass,
      lifetime: mergedTicket?.customData?.CS?.productDetails?.lifetime,
      resalePONumber: mergedTicket?.customData?.CS?.orderDetails?.resalePONumber,
      daysToStrength: mergedTicket?.customData?.CS?.productDetails?.daysToStrength,
      orderedTruckType: mergedTicket?.customData?.CS?.orderDetails?.orderedTruckType,
      ...getIndexedContextKeys(mergedTicket, 'legalText', 'customData.CS.legalText.{i}', 4),
      haulChargeCode: mergedTicket?.customData?.CS?.orderDetails?.haulChargeCode,
      totalCO2Production: mergedTicket?.customData?.CS?.productDetails?.totalCO2Production,
      totalCO2Delivery: mergedTicket?.customData?.CS?.productDetails?.totalCO2Delivery,
      totalCO2: mergedTicket?.customData?.CS?.productDetails?.totalCO2,
      plantServicePhone: mergedTicket?.origin?.customerService?.servicePhone,
      plantServiceEmail: mergedTicket?.origin?.customerService?.email,
      cementClassLongDescription: mergedTicket?.customData?.CS?.productDetails?.cementTypeClass?.name,
    };

    const flat = Object.keys(context).reduce((acc, key) => {
      const v = context[key];

      acc[key] = v == null ? '' : v;

      return acc;
    }, {});

    return { ...mergedTicket, ...flat };
  }, [
    ticketEvents,
    translateMessage,
    primaryLineItem,
    ticket,
    formatLongDateTime,
    cocData,
    batchdata,
    getTruckSeal,
    getSecuenceDataValue,
    ticketEventsFormatted,
    pdfContext,
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return preparedContext;
};
